// ── Sentry browser integration ──────────────────────────────────────────────
// Initializes @sentry/react with MFE-scoped error routing and OTEL cross-ref.
//
// This is the single source of truth for FE Sentry init across the platform
// host AND every agent MFE. It is the superset of the former platform/lib/sentry
// (release tagging, browserTracing, ignoreErrors, replays) and observezen's
// OTEL trace_id cross-reference + MFE tagging. Ref: ASC-5130.

import * as Sentry from '@sentry/react'
import { trace } from '@opentelemetry/api'

import type { SentryConfig } from '../types'

let _initialized = false

/**
 * The product token e2e-tests/helpers/synthetic.ts appends to the browser UA
 * (`… ScorpioE2E/<run-id>`). It is the only marker a browser-side event can
 * carry — the `X-Scorpio-Synthetic` header lives on requests, not on the page.
 */
export const SYNTHETIC_UA_TOKEN = 'ScorpioE2E/'

/** True when this browser is the E2E suite's (UA carries the token). Pure; exported for tests. */
export function isSyntheticUserAgent(userAgent: string | undefined | null): boolean {
  return Boolean(userAgent && userAgent.includes(SYNTHETIC_UA_TOKEN))
}

/**
 * Tag a Sentry event `synthetic: 'true'` when the page runs under the E2E UA.
 * TAG ONLY (ASC-9706 decision): a synthetic error is still a real error, the
 * GlitchTip issue list filters on the tag. Mirrors observezen's backend tag.
 */
export function tagSyntheticEvent<T extends { tags?: Record<string, unknown> }>(
  event: T,
  userAgent: string | undefined | null,
): T {
  if (!isSyntheticUserAgent(userAgent)) return event
  event.tags = { ...event.tags, synthetic: 'true' }
  return event
}

/**
 * Initialize Sentry for browser error tracking. Idempotent and a no-op when
 * `dsn` is empty (Sentry unconfigured for this org / local dev).
 *
 * Adds `otel.trace_id` / `otel.span_id` tags to every Sentry event for
 * cross-referencing with SigNoz traces, plus `mfeName` when supplied.
 * Called once by the host shell (and once per agent shell).
 */
export function initSentry(config: SentryConfig): void {
  if (!config.dsn || _initialized) return
  _initialized = true

  const replayRate = config.replaysSessionSampleRate ?? 0.0
  const replayOnErrorRate = config.replaysOnErrorSampleRate ?? 0.0

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? 'development',
    // Release is supplied by the caller (the platform reads NEXT_PUBLIC_SENTRY_RELEASE,
    // set by CI to the deploy SHA) so this lib stays framework-agnostic. Ref: FR-206.
    release: config.release,
    // Error sampling — almost always 1.0 (capture every error). configzen-driven.
    sampleRate: config.sampleRate ?? 1.0,
    // Web-vitals / FE performance (LCP/INP/CLS) — always-on integration, volume
    // governed by the rate. 0 on GlitchTip (no perf viewer); raise on real Sentry.
    tracesSampleRate: config.tracesSampleRate ?? 0.0,
    // Replay rates are read by the replay integration from these client options;
    // the integration itself is added lazily below only when a rate is non-zero.
    replaysSessionSampleRate: replayRate,
    replaysOnErrorSampleRate: replayOnErrorRate,
    integrations: [Sentry.browserTracingIntegration()],
    // Filter out noisy browser-extension / framework errors.
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'Non-Error promise rejection captured'],

    beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
      // Cross-reference: attach the active OTEL trace_id to every Sentry event.
      const span = trace.getActiveSpan()
      if (span) {
        const ctx = span.spanContext()
        event.tags = {
          ...event.tags,
          'otel.trace_id': ctx.traceId,
          'otel.span_id': ctx.spanId,
        }
      }
      // Tag with MFE name when configured (per-screen attribution).
      if (config.mfeName) {
        event.tags = { ...event.tags, mfeName: config.mfeName }
      }
      // Synthetic-traffic tag (ASC-9706) — read from the UA the E2E suite sets.
      return tagSyntheticEvent(
        event,
        typeof navigator === 'undefined' ? undefined : navigator.userAgent,
      )
    },
  })

  // Lazy Session Replay — the replay SDK is the heaviest chunk and GlitchTip has
  // no replay viewer, so we only pull it in when a replay rate is configured > 0
  // (real Sentry connected). The dynamic import lets the bundler split replay into
  // an async chunk that a GlitchTip-default (rate 0) build never downloads.
  // Ref: ASC-5052 (lazy + config-gated replay).
  if (replayRate > 0 || replayOnErrorRate > 0) {
    void import('@sentry/react')
      .then((S) => {
        Sentry.addIntegration(S.replayIntegration({ maskAllText: true, blockAllMedia: true }))
      })
      .catch(() => {
        // Replay is progressive enhancement; a load failure must not break the app.
        // Surfaced (not silent) so a misconfiguration is visible in dev tooling.
        // eslint-disable-next-line no-console
        console.warn('[observezen] Sentry replay integration failed to load')
      })
  }
}

/**
 * Set Sentry user and org context for the current session.
 *
 * Called by ObservabilityProvider when org/user context changes.
 */
export function setSentryContext(opts: {
  userId?: string
  orgId?: string
  orgSlug?: string
  workspaceId?: string
  mfeName?: string
  agentId?: string
}): void {
  if (opts.userId) {
    Sentry.setUser({ id: opts.userId })
  }
  if (opts.orgId) {
    Sentry.setTag('org_id', opts.orgId)
  }
  if (opts.orgSlug) {
    Sentry.setTag('org_slug', opts.orgSlug)
  }
  if (opts.workspaceId) {
    Sentry.setTag('workspace_id', opts.workspaceId)
  }
  if (opts.mfeName) {
    Sentry.setTag('mfe_name', opts.mfeName)
  }
  if (opts.agentId) {
    Sentry.setTag('agent_id', opts.agentId)
  }
}

/**
 * Report an error to Sentry. The single vendor-neutral capture entry point —
 * callers (platform host, agents) use this instead of importing @sentry/react.
 */
export function captureError(error: unknown): void {
  Sentry.captureException(error)
}

/**
 * Clear the per-agent attribution tags. Call on agent unmount so the next
 * agent's errors don't inherit a stale `agent_id` / `workspace_id`.
 */
export function clearAgentScope(): void {
  Sentry.setTag('agent_id', undefined)
  Sentry.setTag('workspace_id', undefined)
}
